import React from 'react'
import { colors, radius, shadow, fontSize, fontFamily } from '../../styles/theme'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: React.ReactNode
  subtitle?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  width?: number | string
  style?: React.CSSProperties
}

export function Modal({ open, onClose, title, subtitle, children, footer, width = 600, style }: ModalProps) {
  const titleId = React.useId()
  React.useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(10,20,19,0.35)',
        backdropFilter: 'blur(2px)',
        animation: 'docm-fade-in 170ms ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} style={{
        width,
        maxWidth: '95vw',
        maxHeight: '90vh',
        background: colors.surface,
        borderRadius: radius.lg,
        border: `1px solid ${colors.border}`,
        boxShadow: shadow.xl,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'docm-slide-in-up 170ms ease',
        ...style,
      }}>
        {(title || subtitle) && (
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexShrink: 0,
          }}>
            <div>
              <div id={titleId} style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, letterSpacing: '-0.01em' }}>
                {title}
              </div>
              {subtitle && (
                <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 3 }}>
                  {subtitle}
                </div>
              )}
            </div>
            <CloseButton onClick={onClose} />
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {children}
        </div>
        {footer && (
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            flexShrink: 0,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

function CloseButton({ onClick }: { onClick: () => void }) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 28, height: 28, borderRadius: radius.sm,
        border: 'none', background: hovered ? colors.border : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'background 120ms ease',
      }}
    >
      <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
        <path d="M1 1l12 12M13 1L1 13" stroke={colors.textMuted} strokeWidth={1.5} strokeLinecap="round" />
      </svg>
    </button>
  )
}

// ── Stepper (for multi-step modals like Upload Template) ──────────────────
interface StepperProps {
  steps: string[]
  current: number
  style?: React.CSSProperties
}

export function Stepper({ steps, current, style }: StepperProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 0,
      marginBottom: 24,
      ...style,
    }}>
      {steps.map((step, i) => {
        const done = i < current
        const active = i === current
        return (
          <React.Fragment key={step}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? colors.primary : active ? 'transparent' : 'transparent',
                border: `2px solid ${done ? colors.primary : active ? colors.primary : colors.border}`,
                fontFamily: fontFamily.sans,
                fontSize: fontSize.sm,
                fontWeight: 600,
                color: done ? '#fff' : active ? colors.primary : colors.textMuted,
                transition: 'all 170ms ease',
                flexShrink: 0,
              }}>
                {done ? (
                  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : i + 1}
              </div>
              <span style={{
                fontSize: fontSize.xs,
                color: active ? colors.primary : done ? colors.textSecondary : colors.textMuted,
                fontWeight: active ? 500 : 400,
                whiteSpace: 'nowrap',
              }}>
                {step}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1,
                height: 1,
                background: i < current ? colors.primary : colors.border,
                marginTop: -20,
                transition: 'background 170ms ease',
              }} />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}
